import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/store/auth'
import styles from './EmotionCheckIn.module.css'

interface CheckIn { date: string; emotion: string; emoji: string; note: string }
const STORAGE_KEY = 'mellomap-emotion-checkins'
const EMOTIONS = [
  { key: 'happy', emoji: '', label: 'Happy', color: '#E8F5E9' },
  { key: 'okay', emoji: '', label: 'Okay', color: '#FFF3E0' },
  { key: 'worried', emoji: '', label: 'Worried', color: '#FFF9C4' },
  { key: 'sad', emoji: '', label: 'Sad', color: '#E3F2FD' },
  { key: 'frustrated', emoji: '', label: 'Frustrated', color: '#FFEBEE' },
]

function load(): CheckIn[] { const d = localStorage.getItem(STORAGE_KEY); return d ? JSON.parse(d) : [] }
function save(data: CheckIn[]) { localStorage.setItem(STORAGE_KEY, JSON.stringify(data)) }
function today() { return new Date().toISOString().split('T')[0] }

export default function EmotionCheckIn() {
  const navigate = useNavigate()
  const childName = useAuthStore(s => s.user?.child_profile?.name) || 'your child'
  const [checkins, setCheckins] = useState<CheckIn[]>(load)
  const [selected, setSelected] = useState<string | null>(null)
  const [note, setNote] = useState('')

  const todayEntry = checkins.find(c => c.date === today())
  const [month, setMonth] = useState(() => { const d = new Date(); return { m: d.getMonth(), y: d.getFullYear() } })

  const handleSave = () => {
    if (!selected) return
    const emotion = EMOTIONS.find(e => e.key === selected)!
    const entry: CheckIn = { date: today(), emotion: emotion.key, emoji: emotion.emoji, note }
    const updated = [entry, ...checkins.filter(c => c.date !== today())]
    setCheckins(updated); save(updated); setSelected(null); setNote('')
  }

  // Week view
  const weekDays = ['M', 'T', 'W', 'T', 'F', 'S', 'S']
  const getWeekData = () => {
    const now = new Date(); const day = now.getDay(); const diff = now.getDate() - day + (day === 0 ? -6 : 1)
    return weekDays.map((label, i) => {
      const d = new Date(now); d.setDate(diff + i); const ds = d.toISOString().split('T')[0]
      const entry = checkins.find(c => c.date === ds)
      return { label, date: ds, emoji: entry?.emoji || '', isToday: ds === today() }
    })
  }

  // Calendar
  const daysInMonth = new Date(month.y, month.m + 1, 0).getDate()
  const firstDay = (new Date(month.y, month.m, 1).getDay() + 6) % 7
  const calendarDays = Array.from({ length: daysInMonth }, (_, i) => {
    const d = `${month.y}-${String(month.m + 1).padStart(2, '0')}-${String(i + 1).padStart(2, '0')}`
    return { day: i + 1, entry: checkins.find(c => c.date === d) }
  })

  // Stats
  const monthCheckins = checkins.filter(c => { const d = new Date(c.date); return d.getMonth() === month.m && d.getFullYear() === month.y })
  const moodCounts: Record<string, number> = {}
  monthCheckins.forEach(c => { moodCounts[c.emotion] = (moodCounts[c.emotion] || 0) + 1 })
  const topMood = Object.entries(moodCounts).sort((a, b) => b[1] - a[1])[0]

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <button className={styles.backButton} onClick={() => navigate(-1)}>←</button>
        <h1 className={styles.title}>How is {childName} feeling?</h1>
      </header>

      {!todayEntry ? (
        <div className={styles.checkinCard}>
          <p className={styles.checkinLabel}>Today's check-in</p>
          <div className={styles.emotionRow}>
            {EMOTIONS.map(e => (
              <button key={e.key} className={`${styles.emotionBtn} ${selected === e.key ? styles.emotionActive : ''}`} style={{ backgroundColor: selected === e.key ? e.color : 'var(--color-white)' }} onClick={() => setSelected(e.key)}>
                <span className={styles.emotionEmoji}>{e.emoji}</span>
                <span className={styles.emotionLabel}>{e.label}</span>
              </button>
            ))}
          </div>
          {selected && (
            <>
              <textarea className={styles.noteInput} value={note} onChange={e => setNote(e.target.value)} placeholder="Any notes? (optional)" rows={2} />
              <button className={styles.saveBtn} onClick={handleSave}>Save Check-in</button>
            </>
          )}
        </div>
      ) : (
        <div className={styles.checkinDone}>
          <span className={styles.doneEmoji}>{todayEntry.emoji}</span>
          <p className={styles.doneText}>Checked in today: {EMOTIONS.find(e => e.key === todayEntry.emotion)?.label}</p>
          {todayEntry.note && <p className={styles.doneNote}>{todayEntry.note}</p>}
        </div>
      )}

      <div className={styles.weekView}>
        <h3 className={styles.sectionTitle}>This week</h3>
        <div className={styles.weekRow}>
          {getWeekData().map(d => (
            <div key={d.date} className={`${styles.weekDay} ${d.isToday ? styles.weekDayToday : ''}`}>
              <span className={styles.weekLabel}>{d.label}</span>
              <span className={styles.weekEmoji}>{d.emoji || '·'}</span>
            </div>
          ))}
        </div>
      </div>

      <div className={styles.calendarSection}>
        <div className={styles.calendarHeader}>
          <button className={styles.navBtn} onClick={() => setMonth(p => p.m === 0 ? { m: 11, y: p.y - 1 } : { m: p.m - 1, y: p.y })}>‹</button>
          <h3 className={styles.calendarMonth}>{new Date(month.y, month.m).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}</h3>
          <button className={styles.navBtn} onClick={() => setMonth(p => p.m === 11 ? { m: 0, y: p.y + 1 } : { m: p.m + 1, y: p.y })}>›</button>
        </div>
        <div className={styles.calendarGrid}>
          {weekDays.map(d =><div key={d} className={styles.calendarDayLabel}>{d}</div>)}
          {Array.from({ length: firstDay }).map((_, i) =><div key={`e${i}`} />)}
          {calendarDays.map(d => (
            <div key={d.day} className={styles.calendarDay}>{d.entry ? <span>{d.entry.emoji}</span> : <span className={styles.calendarDayNum}>{d.day}</span>}</div>
          ))}
        </div>
      </div>

      {topMood && (
        <div className={styles.stats}>
          <p className={styles.statText}>Most common mood this month: {EMOTIONS.find(e => e.key === topMood[0])?.emoji} {EMOTIONS.find(e => e.key === topMood[0])?.label} ({topMood[1]} days)</p>
        </div>
      )}
    </div>
  )
}
